import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Users, Tractor, CreditCard, UserCheck, Shield, AlertTriangle, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { getAuthHeaders, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [stats, setStats] = useState({ totalFarms: 0, totalFarmers: 0, totalAgronomists: 0, activeSubscriptions: 0 });
  const [farmers, setFarmers] = useState([]);
  const [agronomists, setAgronomists] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const loadAdminData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/admin/stats`, { 
        headers: { 
          ...getAuthHeaders(),
          'Cache-Control': 'no-cache'
        } 
      });
      setStats(response.data.stats || {});
      setFarmers(response.data.farmers || []);
      setAgronomists(response.data.agronomists || []);
      setLastUpdated(new Date());
      if (isRefresh) toast.success('Data refreshed');
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (isRefresh) toast.error('Failed to refresh data');
    } finally { 
      setLoading(false); 
      setRefreshing(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!isAdmin()) { toast.error('Access denied - Admin only'); navigate('/'); return; }
    // Always fetch fresh data on mount/navigation
    loadAdminData();
  }, [isAdmin, navigate, loadAdminData]);

  const filteredFarmers = farmers.filter(f =>
    f.email?.toLowerCase().includes(searchTerm.toLowerCase()) || f.farm_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredAgronomists = agronomists.filter(a =>
    a.email?.toLowerCase().includes(searchTerm.toLowerCase()) || a.agronomist_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <Layout><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 384 }}><div className="spinner" /></div></Layout>;
  }

  return (
    <Layout>
      <div data-testid="admin-page" style={{ maxWidth: 1100 }}>
        {/* Header */}
        <section className="card-floating" style={{ position: 'relative', padding: 22, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg, var(--gold), var(--gold2))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Shield size={20} style={{ color: '#0b241a' }} />
                </div>
                <h1 style={{ fontSize: 24, fontWeight: 700 }}>Administration</h1>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                Manage users, farms, and subscriptions
                {lastUpdated && (
                  <span style={{ marginLeft: 12, fontSize: 11, color: 'var(--text-muted)' }}>
                    • Updated {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>
            <button 
              onClick={() => loadAdminData(true)} 
              disabled={refreshing}
              className="btn-depth-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px' }}
              data-testid="refresh-admin-data"
            >
              <RefreshCw size={16} className={refreshing ? 'spin' : ''} style={{ transition: 'transform 0.3s' }} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </section>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 20 }}>
          {[
            { icon: Tractor, color: 'var(--success)', label: 'Active Farms', value: stats.totalFarms, tid: 'stat-farms' },
            { icon: Users, color: 'var(--info)', label: 'Farmers', value: stats.totalFarmers, tid: 'stat-farmers' },
            { icon: UserCheck, color: '#A855F7', label: 'Agronomists', value: stats.totalAgronomists, tid: 'stat-agronomists' },
            { icon: CreditCard, color: 'var(--warning)', label: 'Subscriptions', value: stats.activeSubscriptions, tid: 'stat-subscriptions' },
          ].map(s => (
            <div key={s.tid} className="card-floating" style={{ position: 'relative', padding: 16 }} data-testid={s.tid}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <s.icon size={17} style={{ color: s.color }} />
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</span>
              </div>
              <p style={{ fontSize: 26, fontWeight: 700 }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <section className="card-floating" style={{ position: 'relative', padding: 14, marginBottom: 20 }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input type="text" placeholder="Search farmers or agronomists..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="input-depth" style={{ paddingLeft: 36 }} data-testid="admin-search" />
          </div>
        </section>

        {/* Farmers */}
        <section className="card-floating" style={{ position: 'relative', overflow: 'hidden', marginBottom: 20 }}>
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={17} style={{ color: 'var(--info)' }} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>Farmers ({filteredFarmers.length})</span>
            </div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {filteredFarmers.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)' }}>
                {farmers.length === 0 ? 'No farmers registered yet' : 'No matching farmers found'}
              </div>
            ) : filteredFarmers.map((farmer, idx) => (
              <div key={farmer.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: idx < filteredFarmers.length - 1 ? '1px solid var(--border-soft)' : 'none' }}
                data-testid={`farmer-row-${farmer.id}`}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 13 }}>{farmer.farm_name || 'Unnamed Farm'}</p>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{farmer.email}</p>
                  {farmer.invited_by_name && (
                    <p style={{ fontSize: 11, color: 'var(--success)', marginTop: 4 }}>
                      Invited by: <strong>{farmer.invited_by_name}</strong>
                    </p>
                  )}
                </div>
                <div style={{ textAlign: 'right', fontSize: 12 }}>
                  <p style={{ color: 'var(--text)' }}>{farmer.fields_count || 0} fields</p>
                  {farmer.farm_size_ha > 0 && (
                    <p style={{ color: 'var(--gold)' }}>{farmer.farm_size_ha.toFixed(1)} ha</p>
                  )}
                  <p style={{ color: 'var(--text-muted)' }}>{new Date(farmer.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Agronomists */}
        <section className="card-floating" style={{ position: 'relative', overflow: 'hidden', marginBottom: 20 }}>
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserCheck size={17} style={{ color: '#A855F7' }} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>Agronomists ({filteredAgronomists.length})</span>
            </div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {filteredAgronomists.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)' }}>
                {agronomists.length === 0 ? 'No agronomists registered yet' : 'No matching agronomists found'}
              </div>
            ) : filteredAgronomists.map((agro, idx) => (
              <div key={agro.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: idx < filteredAgronomists.length - 1 ? '1px solid var(--border-soft)' : 'none' }}
                data-testid={`agronomist-row-${agro.id}`}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 13 }}>{agro.agronomist_name || agro.name || 'Unnamed Agronomist'}</p>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{agro.email}</p>
                </div>
                <div style={{ textAlign: 'right', fontSize: 12 }}>
                  <p style={{ color: '#A855F7' }}>{agro.invited_farmers_count || 0} invited farmers</p>
                  <p style={{ color: 'var(--gold)' }}>{agro.invited_hectares || 0} ha invited</p>
                  <p style={{ color: 'var(--text-muted)' }}>{agro.cases_count || 0} cases handled</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Payments */}
        <section className="card-floating" style={{ position: 'relative', overflow: 'hidden' }}>
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CreditCard size={17} style={{ color: 'var(--warning)' }} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>Monthly Payments</span>
            </div>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.18)', borderRadius: 12, padding: 14 }}>
              <AlertTriangle size={18} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ fontWeight: 600, color: 'var(--warning)', fontSize: 13 }}>Coming Soon</p>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Payment tracking and agronomist payout management will be available in a future update.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Admin;
